import React, { useEffect, useRef, useState } from "react";

// Scroll-triggered reveal wrapper.
// Robust: initial in-view check + IntersectionObserver + scroll fallback.
const Reveal = ({ children, className = "", delay = 0, as: Tag = "div" }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const reveal = () => setVisible(true);

    const inView = () => {
      const r = node.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      return r.top < vh * 0.92 && r.bottom > 0;
    };

    // Already visible on mount
    if (inView()) {
      reveal();
      return;
    }

    let io;
    const cleanup = () => {
      window.removeEventListener("scroll", onScroll);
      if (io) io.disconnect();
    };
    const onScroll = () => {
      if (inView()) {
        reveal();
        cleanup();
      }
    };

    if ("IntersectionObserver" in window) {
      io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              reveal();
              cleanup();
            }
          });
        },
        { threshold: 0.1, rootMargin: "0px 0px -8% 0px" }
      );
      io.observe(node);
    }
    window.addEventListener("scroll", onScroll, { passive: true });

    return cleanup;
  }, []);

  return (
    <Tag
      ref={ref}
      className={`reveal ${visible ? "is-visible" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </Tag>
  );
};

export default Reveal;
